import { createFileRoute } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import { hitlApi, type HitlDecisionRequest, type HitlStatus } from '@/lib/api';
import { PageGuide } from '@/components/PageGuide';
import {
  UserCheck, Clock, CheckCircle2, XCircle, AlertCircle,
} from 'lucide-react';

export const Route = createFileRoute('/_auth/hitl')({
  component: HitlPage,
});

const STATUS_COLORS: Record<HitlStatus, { bg: string; text: string; icon: React.ReactNode }> = {
  PENDING:   { bg: '#f59e0b15', text: '#fbbf24', icon: <Clock size={12} /> },
  APPROVED:  { bg: '#10b98115', text: '#34d399', icon: <CheckCircle2 size={12} /> },
  REJECTED:  { bg: '#ef444415', text: '#f87171', icon: <XCircle size={12} /> },
  TIMED_OUT: { bg: '#6b728015', text: '#94a3b8', icon: <AlertCircle size={12} /> },
};

function StatusBadge({ status }: { status: HitlStatus }) {
  const { bg, text, icon } = STATUS_COLORS[status] ?? STATUS_COLORS.PENDING;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 5,
      padding: '3px 9px', borderRadius: 20, fontSize: 11, fontWeight: 600,
      background: bg, color: text, border: `1px solid ${text}25`,
    }}>
      {icon} {status.replace('_', ' ')}
    </span>
  );
}

function DecisionModal({
  req, onClose, onDecided,
}: {
  req: HitlDecisionRequest;
  onClose: () => void;
  onDecided: () => void;
}) {
  const { t } = useTranslation();
  const [decision, setDecision] = useState<'APPROVED' | 'REJECTED'>('APPROVED');
  const [value, setValue] = useState('');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    try {
      await hitlApi.decide(req.id, { decision, value: value || undefined, note: note || undefined });
      onDecided();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to submit decision');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 }}
      onClick={onClose}
    >
      <form
        onSubmit={submit}
        onClick={e => e.stopPropagation()}
        style={{
          background: 'var(--surface-2, #1e2130)', borderRadius: 14,
          padding: 28, width: 460, display: 'flex', flexDirection: 'column', gap: 16,
          border: '1px solid var(--border, #2d3148)',
        }}
      >
        <h2 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#e2e8f0' }}>Submit Decision</h2>

        {/* Capability + Question context */}
        <div style={{ padding: '12px 14px', borderRadius: 8, background: 'var(--surface-1, #161927)', fontSize: 12 }}>
          <div style={{ color: '#64748b', marginBottom: 4 }}>Capability</div>
          <div style={{ color: '#a5b4fc', fontFamily: 'monospace', marginBottom: 10 }}>{req.capability}</div>
          <div style={{ color: '#64748b', marginBottom: 4 }}>Question from agent</div>
          <div style={{ color: '#cbd5e1', lineHeight: 1.6 }}>{req.question}</div>
          {req.team && (
            <div style={{ marginTop: 8, color: '#64748b', fontSize: 11 }}>
              Routed to: <span style={{ color: '#94a3b8' }}>{req.team.name}</span>
            </div>
          )}
        </div>

        {error && (
          <div style={{ padding: '8px 12px', borderRadius: 8, background: '#ef444415', color: '#fca5a5', fontSize: 12 }}>
            {error}
          </div>
        )}

        {/* Decision radio */}
        <div style={{ display: 'flex', gap: 10 }}>
          {(['APPROVED', 'REJECTED'] as const).map(d => (
            <button
              key={d}
              type="button"
              onClick={() => setDecision(d)}
              style={{
                flex: 1, padding: '9px 0', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600,
                border: decision === d
                  ? `2px solid ${d === 'APPROVED' ? '#10b981' : '#ef4444'}`
                  : '2px solid var(--border, #2d3148)',
                background: decision === d
                  ? (d === 'APPROVED' ? '#10b98115' : '#ef444415')
                  : 'transparent',
                color: decision === d
                  ? (d === 'APPROVED' ? '#34d399' : '#f87171')
                  : '#64748b',
                transition: 'all 0.15s',
              }}
            >
              {d === 'APPROVED' ? `✓ ${t('common.approve')}` : `✗ ${t('common.reject')}`}
            </button>
          ))}
        </div>

        {/* Optional value */}
        <label style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 12, color: '#94a3b8' }}>
          Decision value <span style={{ opacity: 0.6 }}>({t('common.optional')})</span>
          <input
            value={value} onChange={e => setValue(e.target.value)}
            placeholder="Custom value passed back to the agent"
            style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid var(--border, #2d3148)', background: 'var(--surface-1, #161927)', color: '#e2e8f0', fontSize: 13 }}
          />
        </label>

        {/* Note */}
        <label style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 12, color: '#94a3b8' }}>
          Note for agent <span style={{ opacity: 0.6 }}>({t('common.optional')})</span>
          <textarea
            value={note} onChange={e => setNote(e.target.value)}
            rows={2}
            placeholder="Explain your decision to give the agent context"
            style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid var(--border, #2d3148)', background: 'var(--surface-1, #161927)', color: '#e2e8f0', fontSize: 13, resize: 'vertical' }}
          />
        </label>

        <div style={{ display: 'flex', gap: 10 }}>
          <button
            type="button" onClick={onClose}
            style={{ flex: 1, padding: '9px 0', borderRadius: 8, border: '1px solid var(--border, #2d3148)', background: 'none', color: '#94a3b8', cursor: 'pointer', fontSize: 13 }}
          >
            {t('common.cancel')}
          </button>
          <button
            type="submit" disabled={saving}
            style={{
              flex: 1, padding: '9px 0', borderRadius: 8, border: 'none',
              background: decision === 'APPROVED' ? '#10b981' : '#ef4444',
              color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600, opacity: saving ? 0.7 : 1,
            }}
          >
            {saving ? t('common.saving') : 'Submit Decision'}
          </button>
        </div>
      </form>
    </div>
  );
}

export default function HitlPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [statusFilter, setStatusFilter] = useState<HitlStatus | ''>('PENDING');
  const [deciding, setDeciding] = useState<HitlDecisionRequest | null>(null);

  const { data: requests = [], isLoading } = useQuery({
    queryKey: ['hitl', statusFilter],
    queryFn: () => hitlApi.list(statusFilter ? { status: statusFilter as HitlStatus } : undefined),
    refetchInterval: 15_000,
  });

  const filters: Array<{ label: string; value: HitlStatus | '' }> = [
    { label: 'Awaiting decision', value: 'PENDING' },
    { label: 'All', value: '' },
    { label: t('common.approved'), value: 'APPROVED' },
    { label: t('common.rejected'), value: 'REJECTED' },
    { label: t('common.expired'), value: 'TIMED_OUT' },
  ];

  return (
    <div className="page">
      <PageGuide pageKey="hitl" />

      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: '#e2e8f0' }}>{t('nav.hitl')}</h1>
          <p style={{ margin: '4px 0 0', fontSize: 13, color: '#64748b' }}>Human decisions that unblock your agents</p>
        </div>
      </div>

      {/* ── Filters ── */}
      <div style={{ display: 'flex', gap: 8, marginTop: 20, flexWrap: 'wrap' }}>
        {filters.map(f => (
          <button
            key={f.value || 'all'}
            id={`hitl-filter-${f.value || 'all'}`}
            onClick={() => setStatusFilter(f.value)}
            style={{
              padding: '6px 13px', borderRadius: 7, fontSize: 12, fontWeight: 600, cursor: 'pointer',
              border: '1px solid',
              borderColor: statusFilter === f.value ? 'var(--color-primary)' : '#1e2130',
              background: statusFilter === f.value ? 'var(--color-primary-dim)' : 'transparent',
              color: statusFilter === f.value ? 'var(--color-primary)' : '#64748b',
            }}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* ── Loading ── */}
      {isLoading && <div style={{ padding: '40px 0', textAlign: 'center' }}><span style={{ color: '#475569' }}>Loading…</span></div>}

      {!isLoading && requests.length === 0 && (
        <div style={{ padding: '56px 0', textAlign: 'center', border: '1px dashed #1e2130', borderRadius: 12, color: '#475569' }}>
          <UserCheck size={32} style={{ opacity: 0.3, marginBottom: 12 }} />
          <p style={{ margin: 0, fontSize: 14 }}>
            {statusFilter === 'PENDING' ? 'No pending decisions — your agents are running autonomously.' : 'No decisions found for this filter.'}
          </p>
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {requests.map(req => {
          const expiresIn = new Date(req.expiresAt).getTime() - Date.now();
          const expiresMinutes = Math.max(0, Math.round(expiresIn / 60_000));
          const isExpiringSoon = expiresIn > 0 && expiresIn < 5 * 60_000;

          return (
            <div
              key={req.id}
              id={`hitl-${req.id}`}
              style={{
                borderRadius: 12,
                border: `1px solid ${req.status === 'PENDING' && isExpiringSoon ? '#ef4444' : 'var(--border, #2d3148)'}`,
                background: 'var(--surface-2, #1e2130)',
                padding: '16px 20px',
                display: 'flex', gap: 16, alignItems: 'flex-start',
              }}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8, flexWrap: 'wrap' }}>
                  <StatusBadge status={req.status} />
                  <span style={{ fontSize: 11, color: '#a5b4fc', fontFamily: 'monospace', background: '#6366f115', padding: '2px 8px', borderRadius: 10 }}>
                    {req.capability}
                  </span>
                  {req.team && (
                    <span style={{ fontSize: 11, color: '#94a3b8' }}>→ {req.team.name}</span>
                  )}
                  {!req.team && (
                    <span style={{ fontSize: 11, color: '#ef4444', background: '#ef444415', padding: '2px 8px', borderRadius: 10 }}>
                      Unassigned — no team has this capability
                    </span>
                  )}
                </div>

                <p style={{ margin: '0 0 8px', fontSize: 13, color: '#cbd5e1', lineHeight: 1.5 }}>
                  {req.question}
                </p>

                <div style={{ fontSize: 11, color: '#64748b', display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                  {req.status === 'PENDING' && (
                    <span style={{ color: isExpiringSoon ? '#f87171' : '#64748b' }}>
                      <Clock size={10} style={{ marginRight: 3 }} />
                      Expires in {expiresMinutes} min{isExpiringSoon ? ' ⚠' : ''}
                    </span>
                  )}
                  {req.decidedByUser && (
                    <span>Decided by {req.decidedByUser.name ?? req.decidedByUser.email}</span>
                  )}
                  {req.decisionNote && (
                    <span style={{ color: '#94a3b8' }}>Note: {req.decisionNote}</span>
                  )}
                  <span>Conversation: {req.conversationId.slice(0, 8)}…</span>
                </div>
              </div>

              {req.status === 'PENDING' && (
                <button
                  id={`decide-${req.id}`}
                  onClick={() => setDeciding(req)}
                  style={{
                    padding: '8px 16px', borderRadius: 8, border: 'none',
                    background: '#6366f1', color: '#fff', cursor: 'pointer',
                    fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap', flexShrink: 0,
                    transition: 'opacity 0.15s',
                  }}
                >
                  Decide →
                </button>
              )}
            </div>
          );
        })}
      </div>

      {deciding && (
        <DecisionModal
          req={deciding}
          onClose={() => setDeciding(null)}
          onDecided={() => {
            qc.invalidateQueries({ queryKey: ['hitl'] });
            setDeciding(null);
          }}
        />
      )}
    </div>
  );
}
